import { z } from 'zod';
import { RISK_LEVELS } from './incident.js';
import { POLICY_EVENT_TYPES } from './policy.js';
import { DRIVETRAINS } from './vehicle-taxonomy.js';
import { TELEMETRY_HOURLY_AGGREGATES, TELEMETRY_PARAMETER_TIERS, TELEMETRY_TIER_FEATURE, } from './telemetry.js';
/**
 * Catalogue v1 — what a declared query may reference.
 *
 * This is the field list `copilot-catalogue-v1-candidate-register.md` produced
 * by sweeping the canonical model: 22 entries across 6 entities, admitted out of
 * 65 models of which 49 were rejected with reasons. **Nothing executes it yet** —
 * the QueryDocument, its validator and its executor are Phase 3. This is the
 * vocabulary they will be built against, and the thing its drift guard guards.
 *
 * ⚠️ **G2 — nothing is reachable by guessing a column name.** A model may fill a
 * parameter somebody declared; it may never name a field. Everything a query can
 * reference is here, with its type, its unit, its meaning and its permitted
 * aggregations, and anything not here is unreachable rather than merely
 * undocumented.
 *
 * ⚠️ **D18 — a field's MEANING is immutable once admitted.** `type`, `unit`,
 * `population` and the aggregation semantics are a contract, not a description:
 * a saved report built on one of these may be scheduled to an external recipient
 * for years. Labels and meanings may be improved; the four above may not. A
 * change to one of them is a NEW key, and D19 decides whether the old one keeps
 * serving (it can still be computed) or is withdrawn under D17 (it cannot).
 *
 * ⚠️ **Every entry declares its POPULATION, and that is not documentation.**
 * "Distance over ACTIVE vehicles" and "distance over all of them" are two
 * quantities with one name, and nothing about a stored figure says which was
 * meant. It reads as obvious while it is being written and is unrecoverable
 * afterwards.
 *
 * The register's measured findings that shaped this list:
 *
 *  - **Class and drivetrain are 5% and 9% populated**, on disjoint tenants. Both
 *    are admitted and both carry `absence: 'UNCONFIGURED'`, because grouping by
 *    class today answers *"126 of 132: Unclassified"* — correct, useless, and
 *    readable as the platform having lost the data.
 *  - **6% of incidents name a driver**, so `driver` is a subject and "incidents
 *    by driver" is not a dimension.
 *  - **Two sources of distance exist and disagree.** The trip is admitted; a
 *    rollup odometer delta is not.
 *  - **41 of 42 rollup families carry data**, so the MEASURE side is the rich
 *    side and the dimension side is the product gap.
 */
/** What a filter may do to a field. */
export const CATALOGUE_OPERATORS = [
    'eq',
    'in',
    'contains',
    'isNull',
    'gte',
    'lte',
    'window',
];
/**
 * How a figure combines across an organisation's branches (G9).
 *
 * The vocabulary is `FLEET_SUMMARY_MERGE_RULES`'s, deliberately and not
 * coincidentally: the consolidated dashboard and a query answer are read side by
 * side, and two arithmetics for one figure is how a support call becomes an
 * argument about whose number is right.
 */
export const CATALOGUE_MERGES = ['SUM', 'RECOMPUTE', 'IDENTICAL', 'NOT_AGGREGATABLE'];
export const CATALOGUE_AGGREGATIONS = [
    'count',
    'sum',
    'avg',
    'min',
    'max',
    'median',
    'delta',
];
/**
 * Readings that are CUMULATIVE COUNTERS, where the question is how much the
 * counter MOVED rather than what it read.
 *
 * ⚠️ **THIS IS WHY `delta` EXISTS, and the gap it closes was a reported question
 * with no answer.** *"Which vehicles used the most fuel today?"* is unanswerable
 * from `max` or `min` alone: `totalFuelUsedL` is a lifetime total, so its maximum
 * is a vehicle's whole history and its minimum is the same number an hour
 * earlier. Consumption is the DIFFERENCE, and no aggregation expressed one.
 *
 * ⚠️ **MEASURED, not inferred from the names.** All three are monotonic on real
 * data — 331, 309 and 3 hours per vehicle with **zero** negative hourly steps.
 * And `activeDtcCount` is NOT here despite reading like a counter: it is how many
 * fault codes are active NOW, so it falls as faults are fixed, and a delta over
 * it would report a repair as consumption.
 *
 * ⚠️ **A `delta` on a LEVEL is refused, and the distinction is not pedantic.**
 * `fuelLevelPct` moves both ways — a delta over it is a tank filling or emptying,
 * which is a different question from fuel burned and has a different sign. Only a
 * counter that cannot go down can answer "how much was used".
 */
export const CUMULATIVE_TELEMETRY_PARAMETERS = [
    'odometerKm',
    'engineRunHours',
    'totalFuelUsedL',
];
const CUMULATIVE = new Set(CUMULATIVE_TELEMETRY_PARAMETERS);
/** Whether `delta` is meaningful for a reading. */
export function isCumulativeParameter(parameter) {
    return CUMULATIVE.has(parameter);
}
/*
 * ⚠️ **`grain` answers "what is ONE ROW?"; `plural` answers "what are MANY?" — and they
 * are NOT interchangeable.** `plural` exists because `grain` was being used as a count
 * noun and produced grammatical nonsense on a reader's screen: *"over 28 one operational
 * incident records"* and *"no one operational incident records in this window"*. Found by
 * DRIVING a composed answer; every assertion about that sentence passed.
 *
 * ⚠️ **The SAME CLASS of fault is already recorded on `event` below**, where the grain
 * label began as "one canonical event, as a provider reported it" and read as nonsense
 * embedded mid-clause. Twice is a shape, not an accident: a phrase written to describe
 * one row will read badly wherever a noun belongs, so the two are separate fields rather
 * than one phrase asked to do both jobs.
 *
 * `grain` is what a MODEL is shown, so it knows what it is grouping. `plural` is what a
 * READER is shown. A guard asserts every entity declares both and that they differ — a
 * `plural` copied from `grain` would reintroduce the fault silently.
 */
export const CATALOGUE_ENTITIES = {
    vehicle: {
        grain: 'one vehicle that has not been archived',
        plural: 'vehicles',
        model: 'Vehicle',
        evidence: 'the vehicle record',
    },
    incident: {
        grain: 'one operational incident',
        plural: 'incidents',
        model: 'OperationalIncident',
        evidence: 'the incident, its contributing events, and each event’s raw provider payload',
    },
    trip: {
        grain: 'one derived trip',
        plural: 'trips',
        model: 'Trip',
        evidence: 'the trip and the telemetry samples it folded',
    },
    recommendation: {
        grain: 'one recommendation',
        plural: 'recommendations',
        model: 'Recommendation',
        evidence: 'the recommendation, its incident, and the events beneath it',
    },
    driver: {
        grain: 'one driver on the tenant’s roll',
        plural: 'drivers',
        model: 'Driver',
        evidence: 'the driver record',
    },
    /**
     * ⚠️ **Admitted only once GRAIN was explicit (D20), and that ordering was the
     * point.** An incident and its events are two grains over one subject — 205
     * events on 120 incidents, measured — so *"how many over-speeds last week"* is
     * a different number at each and neither is wrong. Offering both before a
     * document had to DECLARE which it meant would have given two correct answers
     * to one question, with nothing on screen able to say why they differed.
     */
    event: {
        /*
         * ⚠️ **Short enough to read INSIDE a sentence**, which every other grain
         * label already was. It began as "one canonical event, as a provider
         * reported it" and the refusals embedded it mid-clause: *"a query about one
         * canonical event, as a provider reported it cannot reach"* — grammatical
         * nonsense in the one message whose whole job is explaining a refusal. Found
         * by reading the output, not by a test. The provenance is where the longer
         * statement belongs.
         */
        grain: 'one canonical event',
        plural: 'events',
        model: 'FleetEvent',
        evidence: 'the event and the raw provider payload it was mapped from',
    },
    'vehicle-hour': {
        grain: 'one vehicle’s readings within one hour',
        plural: 'vehicle-hours',
        model: 'VehicleTelemetryHourly',
        evidence: 'the hour’s rollup row, and the samples behind it while retention holds them',
    },
};
/**
 * Which entities a query at a given grain may REACH for its dimensions.
 *
 * *"Incidents by vehicle class"* is the question this exists for: the grain is
 * the incident, and the class hangs off the vehicle it names. Without a declared
 * reach, every cross-entity question is unaskable and the catalogue is thirteen
 * separate one-table languages.
 *
 * ⚠️ **A DIMENSION may come from a reached entity; a MEASURE may not.** That
 * asymmetry is the whole safety property. Grouping incidents by the class of the
 * vehicle each one names is a LOOKUP — one incident has one vehicle, so no row
 * is counted twice. Summing `trip.distanceMetres` at the incident grain is a
 * FAN-OUT: a vehicle's trips multiply against its incidents and the total is
 * larger than the fleet drove, with nothing on screen saying so. Both are
 * expressible and only one is a number.
 *
 * ⚠️ **Every edge is backed by a real foreign key**, asserted by the drift guard
 * against `Prisma.dmmf` rather than taken on trust — a reach nothing joins is a
 * query that validates and cannot run.
 *
 * `driver` is deliberately NOT reachable from `incident`: the column exists and
 * **6% of incidents carry it**, so grouping by it would silently drop 94% of the
 * population. That is a measured product gap, not a modelling one, and the
 * register defers it rather than admitting a dimension that answers almost
 * nothing.
 */
export const CATALOGUE_GRAIN_REACH = {
    vehicle: [],
    driver: [],
    incident: ['vehicle'],
    trip: ['vehicle'],
    recommendation: ['incident', 'vehicle'],
    /*
     * ⚠️ Vehicle only. An event reaches its incident through `IncidentEvent`, a
     * join table rather than a foreign key — a single event may contribute to more
     * than one — so grouping events by incident type would multiply an event
     * across every incident it fed. That is the fan-out this whole rule exists to
     * refuse, arriving from the many-to-many side.
     */
    event: ['vehicle'],
    'vehicle-hour': ['vehicle'],
};
/**
 * The foreign key behind each edge, and the RELATION that traverses it.
 *
 * The key is what the drift guard proves exists; the relation is what the
 * executor walks. Both, because they answer different questions and a reach with
 * only one of them is either unprovable or unrunnable.
 */
export const CATALOGUE_REACH_KEYS = {
    'incident→vehicle': { model: 'OperationalIncident', field: 'vehicleId', relation: 'vehicle' },
    'trip→vehicle': { model: 'Trip', field: 'vehicleId', relation: 'vehicle' },
    'recommendation→incident': {
        model: 'Recommendation',
        field: 'incidentId',
        relation: 'incident',
    },
    'recommendation→vehicle': { model: 'Recommendation', field: 'vehicleId', relation: 'vehicle' },
    'event→vehicle': { model: 'FleetEvent', field: 'vehicleId', relation: 'vehicle' },
    'vehicle-hour→vehicle': {
        model: 'VehicleTelemetryHourly',
        field: 'vehicleId',
        relation: 'vehicle',
    },
};
/**
 * Where a catalogue entry's value sits WITHIN its own entity.
 *
 * ⚠️ **One definition, three uses — the select, the filter and the grouping.**
 * A dimension read one way for grouping and another for filtering is how a
 * filtered figure stops matching the group it claims to be part of, and neither
 * half looks wrong on its own. The executor derives all three from this.
 *
 * `null` marks an entry with no column of its own: a count, or a value computed
 * from more than one field.
 */
export const CATALOGUE_FIELD_PATHS = {
    'vehicle.status': ['status'],
    // Through the relation, because the NAME is the value and it lives on the class.
    'vehicle.class': ['vehicleClass', 'name'],
    'vehicle.base': ['homeGeofence', 'name'],
    'vehicle.drivetrain': ['drivetrain'],
    'vehicle.registration': ['registration'],
    'vehicle.name': ['name'],
    'incident.type': ['incidentType'],
    'incident.status': ['status'],
    'incident.riskLevel': ['riskLevel'],
    'incident.openedAt': ['openedAt'],
    /*
     * ⚠️ The SAME column as `incident.openedAt`, deliberately. A bucket is a
     * reduction applied after reading, not a different field — so a filter on the
     * instant and a grouping of the day can never disagree about which incidents
     * they are talking about.
     */
    'incident.openedDay': ['openedAt'],
    'incident.openedHour': ['openedAt'],
    'incident.openedWeekday': ['openedAt'],
    'recommendation.status': ['status'],
    'recommendation.actionCategory': ['actionCategory'],
    'trip.distanceSource': ['distanceSource'],
    'driver.status': ['status'],
    'event.type': ['eventType'],
    'event.severity': ['severity'],
    'event.occurredAt': ['occurredAt'],
    'event.count': null,
    'incident.count': null,
    'incident.riskScore': ['currentRiskScore'],
    'incident.occurrenceCount': ['occurrenceCount'],
    // Two instants, so no single column — the executor derives it.
    'incident.resolutionHours': null,
    'trip.distanceMetres': ['distanceMetres'],
    'trip.durationSeconds': ['durationSeconds'],
    'trip.count': null,
    'vehicle.count': null,
    'driver.count': null,
    'recommendation.count': null,
    /*
     * ⚠️ A reading has NO SINGLE COLUMN, and this is the one shape in the
     * catalogue where the aggregation decides what is read. The rollup stores
     * `fuelLevelPct_min`, `_max`, `_avg` and `_n` per hour, so `avg` reads a
     * different column from `max`. `buildQueryPlan` resolves it from the entry's
     * `parameter`; null here means "not one column", not "unreadable".
     */
    'reading.vehicleSpeedKph': null,
    'reading.odometerKm': null,
    'reading.engineRunHours': null,
    'reading.fuelLevelPct': null,
    'reading.batteryVoltageV': null,
    'reading.engineRpm': null,
    'reading.engineLoadPct': null,
    'reading.engineCoolantTempC': null,
    'reading.engineOilTempC': null,
    'reading.engineTempC': null,
    'reading.engineOilPressureKpa': null,
    'reading.engineTorqueActualPct': null,
    'reading.engineTorqueDemandPct': null,
    'reading.intakeManifoldTempC': null,
    'reading.boostPressureKpa': null,
    'reading.exhaustGasTempC': null,
    'reading.transmissionOilTempC': null,
    'reading.ambientTempC': null,
    'reading.dpfSootLoadPct': null,
    'reading.dpfDiffPressureKpa': null,
    'reading.scrInletTempC': null,
    'reading.defLevelPct': null,
    'reading.defLevelLitres': null,
    'reading.defWarningLevel': null,
    'reading.fuelRateLph': null,
    'reading.totalFuelUsedL': null,
    'reading.acceleratorPedalPct': null,
    'reading.brakePedalPct': null,
    'reading.retarderTorquePct': null,
    'reading.airPressurePrimaryKpa': null,
    'reading.airPressureSecondaryKpa': null,
    'reading.grossCombinationWeightKg': null,
    'reading.alternatorVoltageV': null,
    'reading.packVoltageV': null,
    'reading.packCurrentA': null,
    'reading.stateOfChargePct': null,
    'reading.batteryTempC': null,
    'reading.motorTempC': null,
    'reading.regenBrakingKw': null,
    'reading.estimatedRangeKm': null,
    'reading.activeDtcCount': null,
};
/**
 * How to ask whether a value reached through a RELATION is absent.
 *
 * ⚠️ **Prisma's relation filters are WRONG on this schema's composite foreign
 * keys, and wrong in both directions.** Measured against 957 vehicles that all
 * carry no class:
 *
 * | predicate | answer | truth |
 * | --- | --- | --- |
 * | `{ vehicleClass: null }` | 0 | 957 |
 * | `{ vehicleClass: { is: null } }` | 0 | 957 |
 * | `{ vehicleClass: { isNot: null } }` | **957** | 0 |
 * | `{ vehicleClassId: null }` | **957** | 957 |
 *
 * `Vehicle.vehicleClass` is a composite relation — `@relation(fields:
 * [tenantId, vehicleClassId], ...)`, which is how this platform refuses
 * cross-tenant parenting — and the relation filter does not resolve it. The
 * `isNot` row is the dangerous one: it would confidently report that every
 * vehicle HAS a class on a fleet where none does, with nothing failing.
 *
 * So a null test names the FOREIGN KEY COLUMN, never the relation. Declared
 * rather than derived from the path, because which column backs a relation is a
 * fact about the schema; the drift guard proves each one exists.
 */
export const CATALOGUE_ABSENCE_PATHS = {
    'vehicle.class': ['vehicleClassId'],
    /*
     * The same composite-relation trap: `{ homeGeofence: { is: null } }` answers
     * 0 and `{ isNot: null }` answers everything, because the relation is keyed on
     * `(tenantId, homeGeofenceId)`. The FK column is the only predicate that tells
     * the truth — and on day one it is null for every vehicle, so this is the
     * group a reader will open first.
     */
    'vehicle.base': ['homeGeofenceId'],
};
/** The instant a grain is filtered by when a window applies. */
export const CATALOGUE_GRAIN_TIME = {
    // When it was RAISED, never when it last saw an event — an old incident that
    // merged one more event today does not re-enter last week.
    incident: 'openedAt',
    trip: 'startedAt',
    recommendation: 'createdAt',
    /* When the vehicle did it, never when we received it. */
    event: 'occurredAt',
    'vehicle-hour': 'hour',
};
/** Entities a query at `grain` may take a DIMENSION from — itself included. */
export function reachableFrom(grain) {
    return [grain, ...CATALOGUE_GRAIN_REACH[grain]];
}
/**
 * How a timestamp is reduced to something worth grouping by.
 *
 * ⚠️ **A raw timestamp is not a dimension anybody can use.** Grouping on
 * `incident.openedAt` gives one row per incident, which is a list wearing a
 * chart's clothes — so every question about WHEN needs the instant reduced
 * first, and the reduction has to be a DECLARED thing rather than something a
 * caller expresses. That is G1: choose from what is declared, never author.
 *
 * ⚠️ **Every bucket is UTC, and that is a consequence rather than a
 * preference.** `FleetSummaryReadModel` already keys its daily series on a UTC
 * day *"so day boundaries do not shift with the reader"*, and the dashboard
 * draws that series three inches from these. A second convention would put the
 * same incident on two different days on one page.
 *
 * ⚠️ **The cost is real for HOUR_OF_DAY and is stated rather than discovered:**
 * a fleet on British Summer Time reads an hour early all summer. Fixing it
 * needs a tenant display zone, which does not exist — `TenantNotificationPolicy.
 * timeZone` is quiet-hours configuration and borrowing it would make an
 * analytics answer move when somebody edited a notification setting.
 */
export const CATALOGUE_BUCKETS = ['DAY', 'HOUR_OF_DAY', 'WEEKDAY'];
/** Weekday labels, Monday first, in the order a week is read. */
export const CATALOGUE_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/* ────────────────────────────── dimensions ───────────────────────────────── */
const DIMENSIONS = [
    {
        kind: 'DIMENSION',
        key: 'vehicle.status',
        entity: 'vehicle',
        label: 'vehicle status',
        meaning: 'whether the vehicle is in service, out of service, or under maintenance',
        population: 'every vehicle the tenant holds that has not been archived',
        type: 'enum',
        options: ['ACTIVE', 'INACTIVE', 'MAINTENANCE'],
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'Vehicle', field: 'status' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'vehicle.class',
        entity: 'vehicle',
        label: 'vehicle class',
        meaning: 'the class whose median this vehicle is measured against in a cohort comparison',
        population: 'vehicles with a class recorded; 6 of 132 on the development fleet, so most are UNCONFIGURED',
        type: 'enum',
        // Per tenant — `VehicleClass` is tenant-scoped, so there is no fixed list.
        groupable: true,
        operators: ['eq', 'in', 'isNull'],
        merge: 'SUM',
        absence: 'UNCONFIGURED',
        source: { model: 'VehicleClass', field: 'name' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'vehicle.drivetrain',
        entity: 'vehicle',
        label: 'drivetrain',
        meaning: 'what the vehicle is powered by',
        population: 'vehicles with a drivetrain recorded; 12 of 132, so most are UNCONFIGURED',
        type: 'enum',
        options: DRIVETRAINS,
        groupable: true,
        operators: ['eq', 'in', 'isNull'],
        merge: 'SUM',
        absence: 'UNCONFIGURED',
        source: { model: 'Vehicle', field: 'drivetrain' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'vehicle.registration',
        entity: 'vehicle',
        label: 'registration',
        meaning: 'the plate painted on the vehicle; unique per tenant and reserved after archiving',
        population: 'every vehicle — the registration is required',
        type: 'string',
        groupable: true,
        identity: true,
        operators: ['eq', 'contains', 'in'],
        merge: 'IDENTICAL',
        source: { model: 'Vehicle', field: 'registration' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'vehicle.name',
        entity: 'vehicle',
        label: 'vehicle name',
        meaning: 'what the fleet calls this vehicle',
        population: 'every vehicle — the name is required',
        type: 'string',
        groupable: true,
        identity: true,
        operators: ['eq', 'contains', 'in'],
        merge: 'IDENTICAL',
        source: { model: 'Vehicle', field: 'name' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'incident.type',
        entity: 'incident',
        label: 'incident type',
        meaning: 'what kind of finding the correlation rules raised',
        population: 'every incident — the type is required and all seven occur',
        type: 'enum',
        /*
         * ⚠️ The CANONICAL seven, from `EVENT_TYPES`' sibling `INCIDENT_TYPES` in
         * `@oip/domain`. NOT `incidentTypeSchema` in `correlation-config.ts`, which
         * lists FIVE — it is the vocabulary a tenant may classify a geofence kind
         * INTO, and it has not caught up with the safety and security types. Two
         * vocabularies over one concept, which is why assertion 8 of the guard
         * compares against the canonical list rather than against whichever
         * contracts enum is nearest.
         */
        options: [
            'HIGH_RISK_DRIVER_BEHAVIOUR',
            'COMPLIANCE_RISK',
            'ASSET_AVAILABILITY_RISK',
            'VEHICLE_HEALTH_RISK',
            'FUEL_LOSS_RISK',
            'SAFETY_VIOLATION_RISK',
            'SECURITY_RISK',
        ],
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'OperationalIncident', field: 'incidentType' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'incident.status',
        entity: 'incident',
        label: 'incident status',
        meaning: 'where the incident has reached in its lifecycle',
        population: 'every incident; two of the five statuses are in use on the development fleet',
        type: 'enum',
        options: ['OPEN', 'ACKNOWLEDGED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'],
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'OperationalIncident', field: 'status' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'incident.riskLevel',
        entity: 'incident',
        label: 'risk level',
        meaning: 'the band the current risk score fell into',
        population: 'every incident — the level is required and defaults to MEDIUM',
        type: 'enum',
        options: RISK_LEVELS,
        groupable: true,
        // `gte` reads as an INDEX into RISK_LEVELS' ascending order, never as a
        // string comparison — "HIGH and above" is the question people ask.
        operators: ['eq', 'in', 'gte'],
        merge: 'SUM',
        source: { model: 'OperationalIncident', field: 'riskLevel' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'incident.openedAt',
        entity: 'incident',
        label: 'raised',
        meaning: 'when the incident was first raised, not when it last saw an event',
        population: 'every incident',
        type: 'timestamp',
        groupable: true,
        operators: ['window', 'gte', 'lte'],
        // The window is the same question asked of every branch, as the fleet
        // summary asserts of `from`/`to` rather than merging them.
        merge: 'IDENTICAL',
        source: { model: 'OperationalIncident', field: 'openedAt' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'incident.openedDay',
        entity: 'incident',
        label: 'day raised',
        meaning: 'the UTC day the incident was raised, as YYYY-MM-DD',
        population: 'every incident',
        type: 'string',
        bucket: 'DAY',
        groupable: true,
        /*
         * Grouping only. A caller wanting "since the 3rd" filters `incident.openedAt`
         * with `gte`, which is the same column — offering `gte` on a day STRING
         * would compare '2026-09-9' against '2026-09-10' lexically and answer
         * confidently wrong.
         */
        operators: [],
        merge: 'SUM',
        source: { model: 'OperationalIncident', field: 'openedAt' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'incident.openedHour',
        entity: 'incident',
        label: 'hour raised',
        meaning: 'the UTC hour of the day the incident was raised, 00–23. UTC, so a fleet on summer time reads an hour early',
        population: 'every incident',
        type: 'enum',
        options: Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0')),
        bucket: 'HOUR_OF_DAY',
        groupable: true,
        operators: [],
        /*
         * ⚠️ SUM across branches, and that is only true because the hour is UTC.
         * Two branches in different countries would be adding the 09:00 of
         * different mornings, and the merge would be arithmetic over incomparable
         * things — which is exactly what `FLEET_SUMMARY_MERGE_RULES` exists to stop.
         * A tenant display zone would make this MERGE-UNSAFE; there isn't one.
         */
        merge: 'SUM',
        source: { model: 'OperationalIncident', field: 'openedAt' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'incident.openedWeekday',
        entity: 'incident',
        label: 'weekday raised',
        meaning: 'the UTC day of the week the incident was raised, Monday first',
        population: 'every incident',
        type: 'enum',
        options: CATALOGUE_WEEKDAYS,
        bucket: 'WEEKDAY',
        groupable: true,
        operators: [],
        merge: 'SUM',
        source: { model: 'OperationalIncident', field: 'openedAt' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'recommendation.status',
        entity: 'recommendation',
        label: 'recommendation status',
        meaning: 'what an operator did with the advice',
        population: 'every recommendation; three of the five statuses are in use',
        type: 'enum',
        options: ['GENERATED', 'ACKNOWLEDGED', 'ACTIONED', 'DISMISSED', 'SUPERSEDED'],
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'Recommendation', field: 'status' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'recommendation.actionCategory',
        entity: 'recommendation',
        label: 'action category',
        meaning: 'the class of action the recommendation asks somebody to take',
        population: 'every recommendation; eight distinct categories on the development fleet',
        type: 'enum',
        openVocabulary: 'the contract types this z.string().min(1). A closed list was tried once and missed four ' +
            'categories the shipped policies use. An unrecognised category groups under its own name ' +
            'and is never dropped.',
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'Recommendation', field: 'actionCategory' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'trip.distanceSource',
        entity: 'trip',
        label: 'distance source',
        meaning: 'what measured the trip’s distance — the odometer, the positions, or nothing',
        population: 'every trip; 113 ODOMETER and 9 NONE on the development fleet',
        type: 'enum',
        options: ['ODOMETER', 'POSITIONS', 'NONE'],
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'Trip', field: 'distanceSource' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'vehicle.base',
        entity: 'vehicle',
        label: 'base',
        /*
         * ⚠️ **"Based at", and NOT "last seen at".** Two meanings of one English
         * phrase, and the catalogue keeps them apart deliberately (D9): this is a
         * configured assignment, while observation is a provider-asserted enter or
         * exit. "Operated within a geofence" is refused entirely, because OIP
         * computes no containment.
         */
        meaning: 'the depot this vehicle is assigned to — where it is based, not where it was last seen',
        /*
         * ⚠️ Nothing is backfilled, so on day one this is unset for EVERY vehicle.
         * That is the fifth absence state and it is the normal one at first: "no
         * vehicles are based at Leeds" describes an unconfigured platform rather
         * than an empty depot, and the absence rule below is what says which.
         */
        population: 'vehicles with a base assigned; nothing is backfilled, so unset is the starting state',
        type: 'enum',
        // Per tenant — the tenant's own DEPOT-classified geofences.
        groupable: true,
        operators: ['eq', 'in', 'isNull'],
        merge: 'SUM',
        absence: 'UNCONFIGURED',
        source: { model: 'Geofence', field: 'name' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'event.type',
        entity: 'event',
        label: 'event type',
        meaning: 'what the provider reported — the canonical type, not their own word for it',
        population: 'every canonical event; 17 of the 24 declared types occur on the development fleet',
        type: 'enum',
        options: POLICY_EVENT_TYPES,
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'FleetEvent', field: 'eventType' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'event.severity',
        entity: 'event',
        label: 'event severity',
        /*
         * ⚠️ The PROVIDER's severity, and NOT a risk level. `RISK_LEVELS` is what a
         * decision policy computed from an incident; this is what the device said
         * when it reported. Sharing a vocabulary would invite a reader to compare
         * them, and they measure different things at different stages.
         */
        meaning: 'how the provider rated the event when it sent it — not a risk level OIP computed',
        population: 'every canonical event; all three values occur',
        type: 'enum',
        options: ['INFO', 'WARNING', 'CRITICAL'],
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'FleetEvent', field: 'severity' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'event.occurredAt',
        entity: 'event',
        label: 'occurred',
        /*
         * ⚠️ When the VEHICLE did it, never when we received it. A device buffers
         * through a tunnel and uploads on reconnection, so `receivedAt` can be
         * minutes or hours later — and a window built on it would put yesterday's
         * driving in today's answer.
         */
        meaning: 'when the vehicle did it, as the provider timestamped it',
        population: 'every canonical event',
        type: 'timestamp',
        groupable: true,
        operators: ['window', 'gte', 'lte'],
        merge: 'IDENTICAL',
        source: { model: 'FleetEvent', field: 'occurredAt' },
        durable: true,
    },
    {
        kind: 'DIMENSION',
        key: 'driver.status',
        entity: 'driver',
        label: 'driver status',
        meaning: 'whether the driver is on the active roll',
        population: 'every driver who has not been archived',
        type: 'enum',
        options: ['ACTIVE', 'INACTIVE'],
        groupable: true,
        operators: ['eq', 'in'],
        merge: 'SUM',
        source: { model: 'Driver', field: 'status' },
        durable: true,
    },
];
/* ─────────────────────────────── measures ────────────────────────────────── */
/**
 * The five telemetry readings admitted in v1.
 *
 * All `BASIC` tier, so the measure is available to a tenant with a plain tracker
 * and does not silently require `CAN_TELEMETRY`. The other 37 populated families
 * are DEFERRED rather than rejected: their rules are already settled and
 * inherited — `range` families refuse `avg`, `none` families are refused
 * entirely, booleans are refused at any resolution — so admitting them is a
 * register pass each rather than a decision each.
 *
 * ⚠️ **The unit is DECLARED here, never read off the parameter name.**
 * `TELEMETRY_PARAMETERS` declares a KIND and not a unit; the unit lives in the
 * name by convention, and `gearPosition`, `defWarningLevel` and `activeDtcCount`
 * have none anywhere. A naming convention is not a declaration, which is exactly
 * the `durationMs` / `durationSeconds` argument that put units in
 * `POLICY_FIELDS`.
 */
const READINGS = [
    {
        parameter: 'vehicleSpeedKph',
        label: 'speed',
        unit: 'km/h',
        meaning: 'road speed as the vehicle reported it within the hour',
        aggregations: ['avg', 'max', 'min'],
    },
    {
        parameter: 'odometerKm',
        label: 'odometer',
        unit: 'km',
        /*
         * ⚠️ A READING, never a DELTA. `max − min` over a window looks like distance
         * and silently includes odometer resets and replaced units, while
         * `Trip.distanceMetres` declares what measured it and refuses a negative
         * delta. Two sources for one quantity that disagree, and v1 admits one.
         */
        meaning: 'the odometer value the vehicle reported — not a distance travelled',
        aggregations: ['max', 'min'],
    },
    {
        parameter: 'engineRunHours',
        label: 'engine hours',
        unit: 'hours',
        meaning: 'the engine hour counter the vehicle reported',
        aggregations: ['max'],
    },
    {
        parameter: 'fuelLevelPct',
        label: 'fuel level',
        unit: '%',
        meaning: 'how full the tank was, as a percentage of its capacity',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'batteryVoltageV',
        label: 'battery voltage',
        unit: 'V',
        /* The AUXILIARY 12V battery, not an electric vehicle's traction pack. */
        meaning: 'the auxiliary battery voltage, not the traction pack',
        aggregations: ['avg', 'min'],
    },
    /* ── engine, by family ─────────────────────────────────────────────── */
    {
        parameter: 'engineRpm',
        label: 'engine speed',
        unit: 'rpm',
        meaning: 'crankshaft speed as the engine reported it',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'engineLoadPct',
        label: 'engine load',
        unit: '%',
        meaning: 'how hard the engine was working, as a share of what it can deliver',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'engineCoolantTempC',
        label: 'coolant temperature',
        unit: '°C',
        meaning: 'engine coolant temperature',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'engineOilTempC',
        label: 'oil temperature',
        unit: '°C',
        meaning: 'engine oil temperature',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'engineTempC',
        label: 'engine temperature',
        unit: '°C',
        meaning: 'engine block temperature, where the device reports it separately from coolant',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'engineOilPressureKpa',
        label: 'oil pressure',
        unit: 'kPa',
        /* Falling oil pressure at unchanged load is one of the demo's own stories. */
        meaning: 'engine oil pressure',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'engineTorqueActualPct',
        label: 'torque delivered',
        unit: '%',
        meaning: 'torque the engine actually delivered, as a share of its maximum',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'engineTorqueDemandPct',
        label: 'torque demanded',
        unit: '%',
        meaning: 'torque the driver or the ECU asked for, as a share of the maximum',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'intakeManifoldTempC',
        label: 'intake temperature',
        unit: '°C',
        meaning: 'air temperature in the intake manifold',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'boostPressureKpa',
        label: 'boost pressure',
        unit: 'kPa',
        meaning: 'turbocharger boost pressure',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'exhaustGasTempC',
        label: 'exhaust temperature',
        unit: '°C',
        meaning: 'exhaust gas temperature',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'transmissionOilTempC',
        label: 'transmission oil temperature',
        unit: '°C',
        meaning: 'transmission oil temperature',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'ambientTempC',
        label: 'outside temperature',
        unit: '°C',
        /* ⚠️ The only reading here about the WEATHER rather than the vehicle. It
         * explains others — a coolant peak in August is not the same finding as one
         * in February — and is the reason it is admitted at all. */
        meaning: 'air temperature outside the vehicle, as its own sensor read it',
        aggregations: ['avg', 'min', 'max'],
    },
    /* ── emissions and after-treatment ─────────────────────────────────── */
    {
        parameter: 'dpfSootLoadPct',
        label: 'DPF soot load',
        unit: '%',
        meaning: 'how full the particulate filter is, as a share of its capacity',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'dpfDiffPressureKpa',
        label: 'DPF differential pressure',
        unit: 'kPa',
        meaning: 'pressure difference across the particulate filter — it rises as the filter loads',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'scrInletTempC',
        label: 'SCR inlet temperature',
        unit: '°C',
        meaning: 'exhaust temperature entering the SCR catalyst',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'defLevelPct',
        label: 'AdBlue level',
        unit: '%',
        meaning: 'how full the AdBlue tank is, as a share of its capacity',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'defLevelLitres',
        label: 'AdBlue remaining',
        unit: 'L',
        /* ⚠️ A SECOND reading of one tank, in a different unit. Both are stored
         * because devices differ in which they send; a query naming the wrong one
         * gets an empty series rather than a wrong number, which the coverage
         * denominator then explains. */
        meaning: 'AdBlue remaining by volume, where the device reports litres rather than a percentage',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'defWarningLevel',
        label: 'AdBlue warning',
        unit: null,
        /* ⚠️ 0 none … 3 severe, and higher is ALWAYS worse — OIP reverses the
         * provider's own scale, which runs 1 RED to 3 info. `range`, so no average:
         * the mean of a warning level names no state the vehicle was ever in. */
        meaning: 'the AdBlue warning the ECU raised, 0 none to 3 severe — higher is worse',
        aggregations: ['max'],
    },
    /* ── fuel ──────────────────────────────────────────────────────────── */
    {
        parameter: 'fuelRateLph',
        label: 'fuel rate',
        unit: 'L/h',
        meaning: 'fuel burned per hour at the moment of the reading',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'totalFuelUsedL',
        label: 'lifetime fuel used',
        unit: 'L',
        /* ⚠️ A LIFETIME counter, not a consumption figure. Like the odometer, a
         * delta across a window looks like usage and silently includes resets and
         * replaced units — which is why only min and max are offered and the
         * difference is not a declared measure. */
        meaning: 'the vehicle’s lifetime fuel counter — a reading, never a consumption figure',
        aggregations: ['max', 'min'],
    },
    /* ── brakes, air and load ──────────────────────────────────────────── */
    {
        parameter: 'acceleratorPedalPct',
        label: 'accelerator position',
        unit: '%',
        /* ⚠️ PEDAL POSITION, not acceleration. AjnaView sends this as `acceleration`
         * and confirming the meaning is what stopped a harsh-acceleration rule
         * reading a pedal position as m/s². */
        meaning: 'how far the accelerator pedal was pressed — a position, not a rate of change',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'brakePedalPct',
        label: 'brake position',
        unit: '%',
        meaning: 'how far the brake pedal was pressed',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'retarderTorquePct',
        label: 'retarder torque',
        unit: '%',
        meaning: 'retarder braking effort, as a share of its maximum',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'airPressurePrimaryKpa',
        label: 'air pressure (primary)',
        unit: 'kPa',
        meaning: 'primary air-brake circuit pressure',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'airPressureSecondaryKpa',
        label: 'air pressure (secondary)',
        unit: 'kPa',
        meaning: 'secondary air-brake circuit pressure',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'grossCombinationWeightKg',
        label: 'gross weight',
        unit: 'kg',
        meaning: 'the vehicle and its load together, as the vehicle weighed it',
        aggregations: ['avg', 'max'],
    },
    /* ── electrical and electric drivetrain ────────────────────────────── */
    {
        parameter: 'alternatorVoltageV',
        label: 'alternator voltage',
        unit: 'V',
        meaning: 'alternator output voltage',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'packVoltageV',
        label: 'traction pack voltage',
        unit: 'V',
        /* ⚠️ The TRACTION pack, two orders of magnitude above the 12V auxiliary
         * battery already admitted. Both names carry what they measure for exactly
         * this reason. */
        meaning: 'traction battery voltage on an electric vehicle — not the 12V auxiliary battery',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'packCurrentA',
        label: 'traction pack current',
        unit: 'A',
        /* ⚠️ SIGNED: negative is current flowing back INTO the pack. A magnitude
         * would make charging and discharging indistinguishable, which is the one
         * comparison the field exists for. */
        meaning: 'traction battery current — negative while charging or regenerating',
        aggregations: ['avg', 'min', 'max'],
    },
    {
        parameter: 'stateOfChargePct',
        label: 'state of charge',
        unit: '%',
        meaning: 'how full the traction battery is, as a share of its capacity',
        aggregations: ['avg', 'min'],
    },
    {
        parameter: 'batteryTempC',
        label: 'battery temperature',
        unit: '°C',
        meaning: 'traction battery temperature',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'motorTempC',
        label: 'motor temperature',
        unit: '°C',
        meaning: 'traction motor temperature',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'regenBrakingKw',
        label: 'regenerated power',
        unit: 'kW',
        /* ⚠️ An instantaneous RATE, not energy. "kWh recovered today" needs an
         * integration nobody has written, and the sample panel that once claimed
         * one was deleted rather than left invented. */
        meaning: 'power flowing back into the battery at the moment of the reading — a rate, not a total',
        aggregations: ['avg', 'max'],
    },
    {
        parameter: 'estimatedRangeKm',
        label: 'estimated range',
        unit: 'km',
        /* The VEHICLE's own estimate, not OIP's. */
        meaning: 'how far the vehicle believes it can still travel',
        aggregations: ['avg', 'min'],
    },
    /* ── diagnostics ───────────────────────────────────────────────────── */
    {
        parameter: 'activeDtcCount',
        label: 'active fault codes',
        unit: null,
        /* `range`, so no average: the mean of a fault count is not a fault count. */
        meaning: 'how many diagnostic trouble codes the vehicle was showing',
        aggregations: ['max'],
    },
];
const MEASURES = [
    {
        kind: 'MEASURE',
        key: 'incident.count',
        entity: 'incident',
        label: 'incidents',
        meaning: 'how many incidents match',
        population: 'incidents raised within the window, at the incident grain — not their events',
        type: 'count',
        unit: null,
        aggregations: ['count'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'OperationalIncident', field: null },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'incident.riskScore',
        entity: 'incident',
        label: 'risk score',
        meaning: 'the score the tenant’s own decision policy produced, 0–100',
        population: 'every incident; the score is 0 until a policy has judged it',
        type: 'integer',
        unit: null,
        aggregations: ['avg', 'max', 'min'],
        // The type permits it and the meaning does not. See `refused` above.
        refused: ['sum'],
        merge: 'NOT_AGGREGATABLE',
        mergeRefusal: 'an average of branch averages is not the average; ask for it per branch',
        acrossVehicles: true,
        source: { model: 'OperationalIncident', field: 'currentRiskScore' },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'incident.occurrenceCount',
        entity: 'incident',
        label: 'times recurred',
        meaning: 'how many times the same finding merged onto this one incident',
        population: 'every incident; 1 where nothing recurred',
        type: 'integer',
        unit: null,
        aggregations: ['sum', 'avg', 'max'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'OperationalIncident', field: 'occurrenceCount' },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'incident.resolutionHours',
        entity: 'incident',
        label: 'time to resolve',
        meaning: 'hours between an incident being raised and being resolved',
        population: 'RESOLVED incidents only — 13 of 120 on the development fleet, so the floor is the common case',
        type: 'duration',
        unit: 'hours',
        aggregations: ['median', 'avg'],
        refused: [],
        /*
         * Straight from `FLEET_SUMMARY_MERGE_RULES`, which already refuses this for
         * the consolidated dashboard. The mean of medians is not the median.
         */
        merge: 'NOT_AGGREGATABLE',
        mergeRefusal: 'the mean of medians is not the median; the sample size still adds',
        acrossVehicles: true,
        evidenceFloor: 'withheld below the read model’s sample floor, with the COUNT kept — two resolutions out of ' +
            'three is arithmetic, not evidence',
        source: {
            model: 'OperationalIncident',
            field: 'resolvedAt',
            derived: 'resolvedAt − openedAt',
        },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'trip.distanceMetres',
        entity: 'trip',
        label: 'distance',
        meaning: 'how far the vehicle travelled on the trip, by whatever measured it',
        /*
         * ⚠️ NULL is not zero. A trip whose distance nothing measured stores NULL;
         * zero is a vehicle that did not move. `trip.distanceSource` says which, and
         * a sum that treated NULL as 0 would under-report silently.
         */
        population: 'trips with a measured distance; NULL where nothing measured it, never 0',
        type: 'integer',
        unit: 'metres',
        aggregations: ['sum', 'avg', 'max'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'Trip', field: 'distanceMetres' },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'trip.durationSeconds',
        entity: 'trip',
        label: 'trip duration',
        meaning: 'how long the trip lasted, first sample to last',
        population: 'closed trips; an open trip has no duration and is not counted as zero',
        type: 'duration',
        unit: 'seconds',
        aggregations: ['sum', 'avg', 'max'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'Trip', field: 'durationSeconds' },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'trip.count',
        entity: 'trip',
        label: 'trips',
        meaning: 'how many trips match',
        population: 'trips that STARTED within the window',
        type: 'count',
        unit: null,
        aggregations: ['count'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'Trip', field: null },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'event.count',
        entity: 'event',
        label: 'events',
        /*
         * ⚠️ NOT interchangeable with `incident.count`. 205 events landed on 120
         * incidents, so the two answer different questions about the same week and
         * a reader comparing them without the grain in view would conclude one of
         * them was wrong.
         */
        meaning: 'how many canonical events match — one row per report, not per incident',
        population: 'events that occurred within the window, at the event grain',
        type: 'count',
        unit: null,
        aggregations: ['count'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'FleetEvent', field: null },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'recommendation.count',
        entity: 'recommendation',
        label: 'recommendations',
        meaning: 'how many recommendations match',
        /* The same gap the drift guard found on `driver`, one entity along. */
        population: 'recommendations generated within the window, at the recommendation grain',
        type: 'count',
        unit: null,
        aggregations: ['count'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'Recommendation', field: null },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'driver.count',
        entity: 'driver',
        label: 'drivers',
        meaning: 'how many drivers match',
        /*
         * ⚠️ Added because the DRIFT GUARD found the `driver` grain had no measure
         * at all, which made it unaskable: a document must declare at least one
         * measure, so every query at that grain was refused by a rule about
         * something else. The entity was admitted as a subject and then had nothing
         * to count — visible from neither the register's table nor the code.
         */
        population: 'drivers on the roll who have not been archived',
        type: 'count',
        unit: null,
        aggregations: ['count'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'Driver', field: null },
        durable: true,
    },
    {
        kind: 'MEASURE',
        key: 'vehicle.count',
        entity: 'vehicle',
        label: 'vehicles',
        meaning: 'how many vehicles match',
        /*
         * SUM is safe across branches for the reason the fleet summary records: a
         * vehicle belongs to exactly one sub-tenant, so these cannot double-count.
         */
        population: 'vehicles not archived; a vehicle belongs to exactly one branch',
        type: 'count',
        unit: null,
        aggregations: ['count'],
        refused: [],
        merge: 'SUM',
        acrossVehicles: true,
        source: { model: 'Vehicle', field: null },
        durable: true,
    },
    ...READINGS.map((r) => ({
        kind: 'MEASURE',
        key: `reading.${r.parameter}`,
        entity: 'vehicle-hour',
        label: r.label,
        meaning: r.meaning,
        population: 'hours in which this vehicle reported the parameter; an hour with no reading is ABSENT, ' +
            'never zero',
        type: 'number',
        unit: r.unit,
        /*
         * ⚠️ `delta` is added for a CUMULATIVE counter and refused for everything
         * else — see `CUMULATIVE_TELEMETRY_PARAMETERS`. Both halves are derived
         * here rather than declared per reading, because 76 parameters restating a
         * judgement is 76 chances to get one wrong, and the wrong one is silent: a
         * delta over a level reads as consumption.
         */
        aggregations: isCumulativeParameter(r.parameter)
            ? [...r.aggregations, 'delta']
            : r.aggregations,
        // Refusal is DERIVED from the rollup's own declaration, never restated.
        refused: refusedFor(r.parameter, r.aggregations),
        merge: 'NOT_AGGREGATABLE',
        mergeRefusal: 'a reading belongs to one vehicle; ask for it per vehicle',
        acrossVehicles: false,
        parameter: r.parameter,
        /*
         * ⚠️ **The gate is DERIVED from the parameter's own tier**, never written
         * beside it. `TELEMETRY_PARAMETER_TIERS` already decides which readings
         * need a bus interface, and a second copy of that judgement here would let
         * a CAN parameter ship behind the basic gate — selling engine data to a
         * tenant who bought positions. `CAN_TELEMETRY` implies `TELEMETRY`, so a
         * customer holding the upper tier reads both.
         */
        feature: TELEMETRY_TIER_FEATURE[TELEMETRY_PARAMETER_TIERS[r.parameter]],
        source: { model: 'VehicleTelemetryHourly', field: null },
        durable: true,
    })),
];
/**
 * Which aggregations the rollup refuses for a parameter.
 *
 * Derived from `TELEMETRY_HOURLY_AGGREGATES` rather than written out: `range`
 * means min, max and n but NO average, because the mean of a gear number is not
 * a gear and the mean of a warning level names no state the vehicle was in. An
 * unlisted numeric parameter defaults to `full`, which refuses nothing.
 */
function refusedFor(parameter, declared) {
    /*
     * ⚠️ `delta` is refused wherever the parameter is not a cumulative counter,
     * and it is refused EXPLICITLY rather than merely left out of `aggregations`.
     * An undeclared aggregation earns `UNDECLARED_AGGREGATION` — *"declares
     * min, max; delta is not among them"* — which reads as a gap somebody might
     * fill. A refusal earns `REFUSED_AGGREGATION`, which says the MEANING forbids
     * it, and that is the true answer: a delta over a fuel LEVEL is a tank
     * filling, not fuel burned.
     */
    const notCumulative = isCumulativeParameter(parameter)
        ? []
        : ['delta'];
    const rollup = TELEMETRY_HOURLY_AGGREGATES[parameter];
    if (rollup === 'none')
        return ['avg', 'min', 'max', 'sum', 'median', ...notCumulative];
    if (rollup === 'range')
        return ['avg', 'sum', 'median', ...notCumulative];
    // `full`: the mean exists. `sum` is still refused — a sum of readings is not a
    // reading, the same argument `incident.riskScore` makes one entity along.
    return [...['sum', 'median'].filter((a) => !declared.includes(a)), ...notCumulative];
}
export const QUERY_CATALOGUE = [...DIMENSIONS, ...MEASURES];
const BY_KEY = new Map(QUERY_CATALOGUE.map((e) => [e.key, e]));
/** The one lookup. A second copy keyed differently is how the copies start. */
export function catalogueEntry(key) {
    return BY_KEY.get(key);
}
export const catalogueKeySchema = z.enum(QUERY_CATALOGUE.map((e) => e.key));
export const CATALOGUE_DIMENSIONS = DIMENSIONS;
export const CATALOGUE_MEASURES = MEASURES;
//# sourceMappingURL=query-catalogue.js.map